import { bindable, bindingMode, computedFrom, customElement, autoinject } from "aurelia-framework";
import { ValidationRules } from "aurelia-validation";

import nameof from "../../../../common/nameof";
import { FormValidationEnum } from "../../../../enums/form-validation-enum";
import { IAncestorSettings, IInputOptions } from "../../../../interfaces/form-builder/i-input-options";
import {
    IMaxLengthValidation,
    IMinLengthValidation,
    IRegexValidation
} from "../../../../interfaces/form-builder/i-validation";
import { NoteDataManager } from "../../note-data-manager";
import { InputTypesEnum } from "../input-types-enum";
import { ToastrService } from "../../../../services/toastr-service";

@customElement("text-input")
@autoinject
export class TextInput {
    // Input fields
    @bindable({ defaultBindingMode: bindingMode.toView })
    public inputType: string = "";
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public result: any;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public viewOptions: IInputOptions;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public hasHighlight: string = "";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    // INFO: Do not make this field private since it is used in the test file
    public noteDataManager: NoteDataManager;
    public _toastrService: ToastrService;
    public inputTypesEnum: typeof InputTypesEnum = InputTypesEnum;
    public maxLength: number = 65535;
    public minLength: number = 2;

    @computedFrom(nameof<TextInput>("hasHighlight"))
    public get highlightClass() {
        switch (this.hasHighlight) {
            case "errors":
                return "is-invalid";
            case "warnings":
                return "is-warning";
            case "inconsistencies":
                return "is-inconsistency";
        }
        return "";
    }

    @computedFrom(nameof<TextInput>("hasHighlight"))
    public get highlightFocus() {
        return this.hasHighlight.length > 0;
    }

    public set highlightFocus(focus: boolean) {
        // focus would try to set property
    }

    public constructor(noteDataManager: NoteDataManager, toastrService: ToastrService) {
        this.noteDataManager = noteDataManager;
        this._toastrService = toastrService;
    }

    public bind() {
        this.initValidation();
    }

    public onPaste(event: ClipboardEvent) {
        let pastedText = event.clipboardData.getData("text/plain");
        if (typeof pastedText === this.inputType) {
            this.result = pastedText;
        } else {
            this._toastrService.error({
                title: "Error",
                message: "Please enter a valid Input."
            });
        }
    }

    public initValidation() {
        let validation = this.noteDataManager.getValidationFromProp(this.viewOptions.name);
        let validationRules = ValidationRules.ensure((x: any) => x.result);
        validation?.forEach((rule: IMaxLengthValidation | IMinLengthValidation | IRegexValidation) => {
            if (rule.validationType === FormValidationEnum.Required) {
                validationRules.required().withMessage(rule.message).on(this);
            } else if (rule.validationType === FormValidationEnum.MaxLength) {
                this.maxLength = (rule as IMaxLengthValidation).maxLength;
            } else if (rule.validationType === FormValidationEnum.MinLength) {
                this.minLength = (rule as IMinLengthValidation).minLength;
            } else if (rule.validationType === FormValidationEnum.Regex) {
                validationRules
                    .matches(new RegExp((rule as IRegexValidation).pattern))
                    .withMessage(rule.message)
                    .on(this);
            }
        });
    }
}
